/**
 * Profile.tsx · 09_APP · OS-006 · 2026-10-20 · C44, v62, D-0073
 *
 * Her words: "Let's go ahead and build out these profiles, leaving the call and text part grayed out right now
 * while we sort that out... pull up the one that you showed me... It looked like it was good. And we should just
 * use that. We could use that and then add to it if we need to."
 * The approved base is 00_BRAIN/PROPOSALS/COMMUNITY_PROFILE_MOCKUP_2026-10-20.html, ten sections:
 *   1 top card · 2 one-tap actions · 3 next step · 4 at a glance · 5 how they can help · 6 connections ·
 *   7 timeline · 8 giving and helping · 9 keep in touch · 10 email updates and contact details
 *
 * Computer: two columns, like the mockup. Phone (hard rule 18): next step on top, then Timeline / About / Helps.
 * Call and Text are shown GRAYED until the Google Voice transfer is done (her call). Never a tel: link: that dials
 * from her personal cell (hard rule 24).
 * An empty section says so plainly and is never hidden (C44 acceptance test).
 * NOT built because she has not decided: Gmail threads on the timeline (Q2), the check-in rhythm (Q3).
 */

import { useEffect, useState } from "react";
import { supabase } from "../lib/supabase";
import { os, type PersonProfile } from "../lib/os";
import { setAskContext } from "./AskClaude";
import { ReferralQuick } from "./Referral";
import "../impact.css";
import "../profile.css";

const LA = "America/Los_Angeles";
const day = (iso: string | null | undefined) => {
  if (!iso) return "";
  const d = /^\d{4}-\d{2}-\d{2}$/.test(iso) ? new Date(iso + "T12:00:00") : new Date(iso);
  return d.toLocaleDateString("en-US", { timeZone: LA, month: "short", day: "numeric", year: "numeric" });
};
const ago = (iso: string | null) => {
  if (!iso) return null;
  const n = Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
  return n <= 0 ? "today" : n === 1 ? "yesterday" : `${n} days ago`;
};
const money = (n: number) => "$" + Number(n).toLocaleString("en-US", { maximumFractionDigits: 2 });
const todayLA = () => new Date().toLocaleDateString("en-CA", { timeZone: LA });

const REL: Record<string, string> = {
  works_at: "Works at", board_member: "Board member", volunteers_at: "Volunteers at", founder: "Founder",
  funder_contact: "Funder contact", partner_contact: "Partner contact", member_of: "Member of", other: "",
};
const CONN: Record<string, [string, string]> = {   // [when this person is A, when this person is B]
  introduced_by: ["Introduced by", "Introduced you to"], referred_by: ["Referred by", "Referred"],
  mentor_of: ["Mentor of", "Mentored by"], colleague: ["Colleague", "Colleague"], works_for: ["Works for", "Employs"],
  board_peer: ["Board peer", "Board peer"], family: ["Family", "Family"], spouse_partner: ["Partner", "Partner"],
  friend: ["Friend", "Friend"], other: ["Connected", "Connected"],
};
const ICON: Record<string, string> = { email: "✉", meeting: "📅", call: "☎", note: "✎", form: "📝", gift: "💲", card: "🪪", other: "•" };
const FILTERS: Array<[string, string]> = [["all", "All"], ["email", "Email"], ["meeting", "Meetings"], ["call", "Calls"],
  ["note", "Notes"], ["form", "Forms"], ["gift", "Gifts"], ["card", "Cards"]];

type Pane = "timeline" | "about" | "helps";
type Entry = null | "note" | "meeting" | "todo" | "refer";

/** Gravatar takes a SHA-256 of the email; d=404 means "no picture", and then initials show. */
function useGravatar(email: string | undefined) {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!email || !crypto?.subtle) return;
    crypto.subtle.digest("SHA-256", new TextEncoder().encode(email.trim().toLowerCase())).then((b) => {
      const hex = Array.from(new Uint8Array(b)).map((x) => x.toString(16).padStart(2, "0")).join("");
      setUrl(`https://gravatar.com/avatar/${hex}?s=160&d=404`);
    });
  }, [email]);
  return url;
}

export function Profile({ id, onBack }: { id: string; onBack: () => void }) {
  const [p, setP] = useState<PersonProfile | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [photos, setPhotos] = useState<string[]>([]);
  const [pane, setPane] = useState<Pane>("timeline");
  const [filter, setFilter] = useState("all");
  const [entry, setEntry] = useState<Entry>(null);
  const [draft, setDraft] = useState("");
  const [due, setDue] = useState("");
  const [saving, setSaving] = useState(false);
  const [glanceEdit, setGlanceEdit] = useState<string | null>(null);
  const [helpNew, setHelpNew] = useState("");
  const [faceOk, setFaceOk] = useState(true);
  const face = useGravatar(p?.emails[0]?.email);

  useEffect(() => {
    os.personProfile(id).then(async (x) => {
      setP(x);
      setAskContext({ page: "profile", personId: id, personName: x.person?.name ?? null });
      const urls = await Promise.all(x.cards.map(async (c) =>
        (await supabase.storage.from("card-scans").createSignedUrl(c.storage_path, 600)).data?.signedUrl ?? ""));
      setPhotos(urls.filter(Boolean));
    }).catch((e) => setErr(e instanceof Error ? e.message : String(e)));
    return () => setAskContext({ page: "people", personId: null, personName: null });
  }, [id]);

  async function run(f: () => Promise<PersonProfile>) {
    setSaving(true); setErr(null);
    try { setP(await f()); return true; }
    catch (e) { setErr(e instanceof Error ? e.message : String(e)); return false; }
    finally { setSaving(false); }
  }
  async function saveEntry() {
    const ok = entry === "todo" ? await run(() => os.personTaskAdd(id, draft, due || null))
      : entry === "note" || entry === "meeting" ? await run(() => os.personLog(id, entry, draft)) : false;
    if (ok) { setEntry(null); setDraft(""); setDue(""); }
  }

  if (err && !p) return <main className="people"><button className="pp-back" onClick={onBack}>‹ Back</button><div className="refusebox">{err}</div></main>;
  if (!p) return <main className="people"><p className="muted">Opening…</p></main>;
  if (!p.person) return <main className="people"><button className="pp-back" onClick={onBack}>‹ Back</button><p>Not found.</p></main>;
  const pr = p.person;
  const initials = pr.name.split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]!.toUpperCase()).join("");
  const email = p.emails[0]?.email;
  const next = p.tasks[0] ?? null;
  const talked = ago(p.last_talked);
  const tl = p.timeline.filter((t) => filter === "all" || t.type === filter);

  // ---------------------------------------------------------------- sections (each is reused on phone and computer)
  const sNext = (
    <section className="pf-sec">
      <h3>Next step</h3>
      {next ? (
        <div className="pf-next">
          <b>{next.title}</b>
          <span>{next.due_on ? `Due ${day(next.due_on)}${next.due_on <= todayLA() ? " · on Today" : ""}` : "No due date"}</span>
          <button disabled={saving} onClick={() => void run(() => os.personTaskDone(next.id))}>Done</button>
        </div>
      ) : <p className="pf-empty">No next step yet. Tap To-do to add one.</p>}
      {p.tasks.length > 1 && <ul className="pf-more">{p.tasks.slice(1).map((t) => (
        <li key={t.id}><span>{t.title}{t.due_on ? ` · ${day(t.due_on)}` : ""}</span>
          <button disabled={saving} onClick={() => void run(() => os.personTaskDone(t.id))}>Done</button></li>))}</ul>}
    </section>
  );
  const sGlance = (
    <section className="pf-sec">
      <h3>At a glance {glanceEdit === null && <button className="pf-link" onClick={() => setGlanceEdit(pr.notes ?? "")}>edit</button>}</h3>
      {glanceEdit === null
        ? (pr.notes ? <p className="pf-glance">{pr.notes}</p> : <p className="pf-empty">How you met, what they care about, talking points. Nothing yet.</p>)
        : (<div className="pf-form">
            <textarea value={glanceEdit} onChange={(e) => setGlanceEdit(e.target.value)} rows={4}
              placeholder="How you met, what they care about, talking points" />
            <div><button className="pf-primary" disabled={saving}
              onClick={async () => { if (await run(() => os.personGlance(id, glanceEdit))) setGlanceEdit(null); }}>Save</button>
              <button onClick={() => setGlanceEdit(null)}>Cancel</button></div>
          </div>)}
    </section>
  );
  const sHelp = (
    <section className="pf-sec">
      <h3>How they can help C2C</h3>
      {p.help.length > 0
        ? <div className="pf-tags">{p.help.map((h) => (
            <span key={h.id}>{h.label}<button aria-label={`Remove ${h.label}`} disabled={saving}
              onClick={() => void run(() => os.personHelpRemove(h.id))}>×</button></span>))}</div>
        : <p className="pf-empty">Skills, resources, doors they can open. Nothing yet.</p>}
      <div className="pf-add">
        <input value={helpNew} onChange={(e) => setHelpNew(e.target.value)} placeholder="e.g. Jobs: warehouse, Mentor, Knows the Chamber board"
          onKeyDown={async (e) => { if (e.key === "Enter" && helpNew.trim() && await run(() => os.personHelpAdd(id, helpNew))) setHelpNew(""); }} />
        <button disabled={saving || !helpNew.trim()} onClick={async () => { if (await run(() => os.personHelpAdd(id, helpNew))) setHelpNew(""); }}>Add</button>
      </div>
    </section>
  );
  const sConn = (
    <section className="pf-sec">
      <h3>Connections</h3>
      {p.organizations.length === 0 && p.connections.length === 0 && !pr.organisation && <p className="pf-empty">No organizations or people linked yet.</p>}
      {p.organizations.length === 0 && pr.organisation && <p className="pf-kv">🏢 {[pr.organisation, pr.job_title].filter(Boolean).join(", ")}</p>}
      {p.organizations.map((o, k) => <p key={k} className="pf-kv">🏢 {o.name}{o.title ? `, ${o.title}` : REL[o.relationship] ? ` · ${REL[o.relationship]}` : ""}</p>)}
      {p.connections.map((c) => <p key={c.id + c.kind} className="pf-kv">🤝 {(CONN[c.kind] ?? CONN.other!)[c.mine ? 0 : 1]}: <a href={`#people/${c.id}`}>{c.name}</a></p>)}
    </section>
  );
  const sGiving = (
    <section className="pf-sec">
      <h3>Giving and helping</h3>
      <p className="pf-kv"><b>Gifts:</b> {p.giving.count === 0 ? "none recorded"
        : `${money(p.giving.this_year)} this year · ${money(p.giving.all_time)} all time${p.giving.last_on ? ` · last ${day(p.giving.last_on)}` : ""}`}</p>
      <p className="pf-kv"><b>Volunteer hours:</b> {Number(p.volunteer_hours) > 0 ? p.volunteer_hours : "none recorded"}</p>
    </section>
  );
  const sKeep = (
    <section className="pf-sec">
      <h3>Keep in touch</h3>
      <p className="pf-kv"><b>Last talked:</b> {p.last_talked ? `${day(p.last_talked)} (${talked})` : "no conversation logged yet"}</p>
      <p className="pf-empty">How often to check in is not set yet.</p>
    </section>
  );
  const sEmailUpd = (
    <section className="pf-sec">
      <h3>Email updates</h3>
      <p className="pf-kv">{p.on_mailing_list ? "On the mailing list." : "Not on the mailing list."}</p>
      {p.consent.map((c) => <p key={c.channel} className="pf-kv"><b>{c.channel}:</b> {c.may_send ? "yes" : "no"}</p>)}
    </section>
  );
  const sContact = (
    <section className="pf-sec">
      <h3>Contact</h3>
      {p.emails.map((e) => <a key={e.email} className="pf-row" href={`mailto:${e.email}`}>{e.email}</a>)}
      {p.phones.map((ph) => <p key={ph.phone} className="pf-row">{ph.phone}{ph.label ? ` · ${ph.label}` : ""}</p>)}
      {p.emails.length === 0 && p.phones.length === 0 && <p className="pf-empty">No email or phone on file.</p>}
      <p className="pf-kv"><b>Came in:</b> {pr.source ?? "unknown"}{pr.source_date ? `, ${day(pr.source_date)}` : ""}</p>
      {photos.map((u) => <img key={u} className="pp-photo" src={u} alt="Business card" />)}
    </section>
  );
  const sAcademy = p.enrollments.length > 0 && (
    <section className="pf-sec">
      <h3>Academy</h3>
      {p.enrollments.map((e, i) => <p key={i} className="pf-kv">{e.stage}{e.applied_on ? `, applied ${day(e.applied_on)}` : ""}</p>)}
    </section>
  );
  const sTimeline = (
    <section className="pf-sec">
      <h3>Timeline</h3>
      <div className="pf-filters">{FILTERS.map(([k, label]) => (
        <button key={k} className={filter === k ? "on" : ""} onClick={() => setFilter(k)}>{label}</button>))}</div>
      {tl.length === 0 && <p className="pf-empty">{filter === "all" ? "Nothing logged yet." : "Nothing of this kind yet."}</p>}
      <ul className="pf-tl">{tl.map((t, k) => (
        <li key={k}><span className="ic">{ICON[t.type] ?? "•"}</span>
          <div><b>{t.title}</b>{t.detail && t.detail !== t.title && <em>{t.detail}</em>}
            <small>{t.at ? day(t.at) : "date not recorded"}{t.source === "cloze" ? " · from Cloze" : ""}</small></div></li>))}</ul>
    </section>
  );

  const soon = "Coming soon: calls and texts will go out on the C2C Google Voice line";
  return (
    <main className="people pf">
      <button className="pp-back" onClick={onBack}>‹ Back</button>
      <div className="pf-frame">
        {/* 1 · top card */}
        <header className="pf-top">
          <div className="pf-face">{face && faceOk ? <img src={face} alt="" onError={() => setFaceOk(false)} /> : initials}</div>
          <div>
            <h2>{pr.name}</h2>
            {(pr.job_title || pr.organisation) && <p>{[pr.job_title, pr.organisation].filter(Boolean).join(", ")}</p>}
            {/* D-0075, her words: "keep our platform very simple". One line, then only what they ARE and how long ago. */}
            <p className="pf-since">Connected since {day(pr.source_date ?? pr.created_at)}</p>
            <div className="pf-chips">
              {p.roles.map((r) => <span key={r}>{r}</span>)}
              {talked && <span className="o">{talked}</span>}
            </div>
          </div>
        </header>

        {/* 2 · one-tap actions */}
        <nav className="pf-actions" aria-label="Actions">
          {email ? <a href={`mailto:${email}`}><span>✉</span>Email</a> : <button disabled title="No email on file"><span>✉</span>Email</button>}
          <button disabled title={soon} aria-label={`Call. ${soon}`}><span>☎</span>Call<small>soon</small></button>
          <button disabled title={soon} aria-label={`Text. ${soon}`}><span>💬</span>Text<small>soon</small></button>
          <button className={entry === "note" ? "on" : ""} onClick={() => setEntry(entry === "note" ? null : "note")}><span>✎</span>Note</button>
          <button className={entry === "todo" ? "on" : ""} onClick={() => setEntry(entry === "todo" ? null : "todo")}><span>✓</span>To-do</button>
          <button className={entry === "meeting" ? "on" : ""} onClick={() => setEntry(entry === "meeting" ? null : "meeting")}><span>📅</span>Meeting</button>
          {/* C47: one tap to log that we referred her out. */}
          <button className={entry === "refer" ? "on" : ""} onClick={() => setEntry(entry === "refer" ? null : "refer")}><span>↗</span>Refer</button>
        </nav>
        {entry === "refer" && (
          <div className="pf-entry">
            <ReferralQuick personId={id} personName={pr.name} onClose={() => setEntry(null)}
              onSaved={() => { void os.personProfile(id).then(setP); }} />
          </div>
        )}
        {entry && entry !== "refer" && (
          <div className="pf-entry pf-form">
            <textarea autoFocus rows={3} value={draft} onChange={(e) => setDraft(e.target.value)}
              placeholder={entry === "note" ? "A note about them" : entry === "meeting" ? "What you talked about" : "The next step, e.g. Ask about hiring Academy alumni"} />
            {entry === "todo" && <label>Due <input type="date" value={due} onChange={(e) => setDue(e.target.value)} /></label>}
            <div><button className="pf-primary" disabled={saving || !draft.trim()} onClick={() => void saveEntry()}>Save</button>
              <button onClick={() => { setEntry(null); setDraft(""); }}>Cancel</button></div>
          </div>
        )}
        {err && <div className="refusebox">{err}</div>}

        {/* phone: 3 · next step above the tabs, then Timeline / About / Helps */}
        <div className="pf-phone-next">{sNext}</div>
        <div className="pf-tabs" role="tablist">
          {(["timeline", "about", "helps"] as Pane[]).map((k) => (
            <button key={k} role="tab" aria-selected={pane === k} className={pane === k ? "on" : ""} onClick={() => setPane(k)}>
              {k === "timeline" ? "Timeline" : k === "about" ? "About" : "Helps"}</button>))}
        </div>

        <div className="pf-cols" data-pane={pane}>
          <aside className="pf-side">
            <div className="pf-desk-next">{sNext}</div>
            <div className="pane-about">{sGlance}</div>
            <div className="pane-helps">{sHelp}</div>
            <div className="pane-about">{sConn}</div>
            <div className="pane-helps">{sGiving}</div>
            <div className="pane-about">{sKeep}</div>
            <div className="pane-about">{sEmailUpd}</div>
            <div className="pane-about">{sContact}</div>
            {sAcademy && <div className="pane-about">{sAcademy}</div>}
          </aside>
          <div className="pf-main pane-timeline">{sTimeline}</div>
        </div>
      </div>
    </main>
  );
}
